import { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import CustomizeTableview from "../../Components/TableViews/CustomizeTableview";
import PaginationBar from "../../Components/PaginationBar";
import NoResult from "../../Components/NoResult";
import Loader from "../../Components/Loader";
import SearchableSelect from "../../Components/SearchableSelect";
import PopUpModal from "../../Components/Modal";
import useApi from "../../Hooks/useApi";
import * as api from "../../api/getapi";
import * as postapi from "../../api/postapi";
import ListingReviewsDummyUpsertModal from "./ListingReviewsDummyUpsertModal";
import ListingReviewsDummyBulkUploadModal from "./ListingReviewsDummyBulkUploadModal";

const ListingReviewsDummy = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [noOfPages, setNoOfPages] = useState(1);
  const [sizeOfPages, setSizeOfPages] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderBy, setOrderBy] = useState({ created_at: "DESC" });
  const [toggle, refresh] = useState(false);

  const [listingIdInput, setListingIdInput] = useState("");
  const [isPendingFilter, setIsPendingFilter] = useState({ value: null, label: "All" });

  const [reviews, setReviews] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  const [upsertModalOpen, setUpsertModalOpen] = useState(false);
  const [upsertInitialData, setUpsertInitialData] = useState(null);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);

  const fetchDummyListingReviews = useApi(api.fetchDummyListingReviews, { hasCatchError: true });
  const fetchDummyListingReviewsNoOfPages = useApi(api.fetchDummyListingReviewsNoOfPages, { hasCatchError: true });
  const deleteDummyListingReview = useApi(postapi.deleteDummyListingReview, { hasCatchError: true });

  const parseListingId = () => {
    const trimmed = String(listingIdInput || "").trim();
    if (!trimmed.length) return null;
    const asNumber = Number(trimmed);
    if (!Number.isFinite(asNumber)) return null;
    return asNumber;
  };

  const changePage = (page) => {
    setCurrentPage(page);
  };

  const resetFilterHandle = () => {
    setListingIdInput("");
    setIsPendingFilter({ value: null, label: "All" });
    setSizeOfPages(20);
    setOrderBy({ created_at: "DESC" });
  };

  const loadNoOfPages = async () => {
    const listingId = parseListingId();
    const isPending = typeof isPendingFilter?.value === "number" ? isPendingFilter.value : null;

    const res = await fetchDummyListingReviewsNoOfPages.request({
      size: sizeOfPages,
      ...(typeof listingId === "number" ? { listingId } : {}),
      ...(typeof isPending === "number" ? { isPending } : {}),
    });

    const meta = res?.data?.data;
    setNoOfPages(meta?.no_of_pages ?? 0);
    setTotalItems(meta?.no_of_items ?? 0);
  };

  const loadReviews = async () => {
    const listingId = parseListingId();
    const isPending = typeof isPendingFilter?.value === "number" ? isPendingFilter.value : null;

    const mapOrderByForApi = (orderObj) => {
      if (!orderObj) return orderObj;
      const [[key, value]] = Object.entries(orderObj);
      if (key === "listing_id") return { fk_listing_id: value };
      return orderObj;
    };

    const res = await fetchDummyListingReviews.request({
      page: currentPage,
      size: sizeOfPages,
      orderBy: mapOrderByForApi(orderBy),
      ...(typeof listingId === "number" ? { listingId } : {}),
      ...(typeof isPending === "number" ? { isPending } : {}),
    });

    const data = res?.data?.data;
    setReviews(Array.isArray(data) ? data : []);
  };

  const tableRows = reviews.map((row) => {
    return {
      id: row?.id,
      listing_id: row?.fk_listing_id,
      rating: row?.rating,
      review: row?.review,
      is_pending: row?.is_pending === 1 ? "Yes" : "No",
      is_deleted: row?.is_deleted === 1 ? "Yes" : "No",
      created_at: row?.created_at,
      updated_at: row?.updated_at,
      __raw: row,
    };
  });

  useEffect(() => {
    setIsLoading(true);
    Promise.all([loadNoOfPages(), loadReviews()])
      .catch(() => {
        setNoOfPages(0);
        setTotalItems(0);
        setReviews([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [currentPage, sizeOfPages, orderBy, toggle, isPendingFilter, listingIdInput]);

  useEffect(() => {
    if (currentPage === 1) {
      refresh((prev) => !prev);
    } else {
      changePage(1);
    }
  }, [sizeOfPages, orderBy, isPendingFilter, listingIdInput]);

  const openCreate = () => {
    setUpsertInitialData(null);
    setUpsertModalOpen(true);
  };

  const openEdit = (row) => {
    setUpsertInitialData(row?.__raw ?? null);
    setUpsertModalOpen(true);
  };

  const remove = async (row) => {
    try {
      await deleteDummyListingReview.request(row?.__raw?.id);
      toast.success("Dummy review deleted successfully", {
        position: "bottom-right",
        autoClose: 1500,
      });
      refresh((prev) => !prev);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Failed to delete dummy review", {
        position: "bottom-right",
        autoClose: 1500,
      });
    }
  };

  return (
    <>
      <Container fluid>
        <Row className="d-flex align-items-center">
          <Col xs="6" lg="2" className="mb-3">
            <SearchableSelect
              selectClassName={"bg-white"}
              placeholder={`Showing ${sizeOfPages}`}
              defaultValue={`Showing ${sizeOfPages}`}
              value={`Showing ${sizeOfPages}`}
              searchable={false}
              options={[
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
              handleChange={(e) => setSizeOfPages(Number(e.value))}
            />
          </Col>

          <Col xs="12" lg="3" className="mb-3">
            <Form.Control
              className="bg-white"
              placeholder="Filter by listingId (optional)"
              value={listingIdInput}
              onChange={(e) => {
                const next = e.target.value;
                if (/^\d*$/.test(next)) {
                  setListingIdInput(next);
                }
              }}
            />
          </Col>

          <Col xs="12" lg="3" className="mb-3">
            <SearchableSelect
              selectClassName={"bg-white"}
              placeholder="Filter isPending"
              searchable={false}
              value={isPendingFilter}
              options={[
                { value: null, label: "All" },
                { value: 0, label: "Approved" },
                { value: 1, label: "Pending" },
              ]}
              handleChange={(e) => {
                if (typeof e?.value === "number") {
                  setIsPendingFilter({ value: e.value, label: e.label });
                } else {
                  setIsPendingFilter({ value: null, label: "All" });
                }
              }}
            />
          </Col>

          <Col xs="12" lg="2" className="mb-3">
            <Button className="w-100" size="sm" onClick={openCreate}>
              Create Dummy Review
            </Button>
          </Col>

          <Col xs="12" lg="2" className="mb-3">
            <Button className="w-100" size="sm" variant="outline-primary" onClick={() => setBulkUploadModalOpen(true)}>
              Bulk Upload
            </Button>
          </Col>
        </Row>

        <div className="pt-2">
          <Button size="sm" type="button" style={{ fontSize: "14px" }} onClick={resetFilterHandle}>
            <span className="d-lg-inline">Reset Filters</span>{" "}
          </Button>
        </div>

        {isLoading ? (
          <div style={{ height: "50vh", display: "flex", justifyContent: "center" }}>
            <Loader />
          </div>
        ) : reviews?.length === 0 ? (
          <NoResult name="Dummy Reviews" />
        ) : (
          <>
            <CustomizeTableview
              className={"mt-3"}
              data={tableRows}
              columnNotShow={["id", "__raw", "img0", "img1"]}
              datetimeColumn={["created_at", "updated_at"]}
              sortColumn={["created_at", "updated_at", "rating", "listing_id", "is_pending"]}
              collapsableColumns={["review"]}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              edit={openEdit}
              remove={remove}
            />
            <Row className="mt-5 align-items-center">
              <Col className="d-none d-md-block" md="3" lg="3">
                <span className="text-secondary fs-6">{totalItems} result(s)</span>
              </Col>
              <Col xs="12" md="6" lg="6">
                <PaginationBar currentPage={currentPage} noOfPages={noOfPages} changePage={changePage} />
              </Col>
              <Col className="d-md-none d-flex align-items-center mt-3" xs="6" lg="3">
                <span className="text-secondary fs-6">{totalItems} result(s)</span>
              </Col>
            </Row>
          </>
        )}
      </Container>

      <PopUpModal
        title={upsertInitialData ? "Edit Dummy Review" : "Create Dummy Review"}
        show={upsertModalOpen}
        onHide={() => {
          setUpsertModalOpen(false);
          setUpsertInitialData(null);
        }}
      >
        <ListingReviewsDummyUpsertModal
          initialData={upsertInitialData}
          onSuccess={() => {
            setUpsertModalOpen(false);
            setUpsertInitialData(null);
            refresh((prev) => !prev);
          }}
          onCancel={() => {
            setUpsertModalOpen(false);
            setUpsertInitialData(null);
          }}
        />
      </PopUpModal>

      <PopUpModal
        size="lg"
        title="Bulk Upload Dummy Reviews"
        show={bulkUploadModalOpen}
        onHide={() => setBulkUploadModalOpen(false)}
      >
        <ListingReviewsDummyBulkUploadModal
          onSuccess={() => {
            setBulkUploadModalOpen(false);
            refresh((prev) => !prev);
          }}
          onCancel={() => setBulkUploadModalOpen(false)}
        />
      </PopUpModal>
    </>
  );
};

export default ListingReviewsDummy;
